import React, {useEffect, useRef} from 'react';
import {BrandName} from "../../constants";
import {useNotificationMessage} from "../../hooks/NotificationContext";

interface IHeroProps {
    isLight: boolean;
}

const canvasSize = 500;
export const Hero: React.FC<IHeroProps> = ({isLight}) => {
    const {showNotification} = useNotificationMessage();

    const canvasRef = useRef<HTMLCanvasElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (canvasRef.current) {
            drawSampleImage(canvasRef.current);
        }
    }, []);

    // 绘制示例图像
    const drawSampleImage = (canvas: HTMLCanvasElement) => {
        const ctx = canvas.getContext('2d');
        if (!ctx) return ;

        const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
        gradient.addColorStop(0, '#6366f1');
        gradient.addColorStop(1, '#f59e0b');

        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        ctx.fillStyle = 'white';
        ctx.font = 'bold 30px Poppins';
        ctx.textAlign = 'center';
        ctx.fillText(BrandName, canvas.width/2, canvas.height/2 - 20);

        ctx.font = '16px Poppins';
        ctx.fillText('Upload Photo And Edit It', canvas.width/2, canvas.height/2 + 20);

        // 添加一些装饰元素
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
        ctx.lineWidth = 2;

        // 左上角装饰
        ctx.beginPath();
        ctx.moveTo(20, 20);
        ctx.lineTo(80, 20);
        ctx.lineTo(20, 80);
        ctx.stroke();

        // 右下角装饰
        ctx.beginPath();
        ctx.moveTo(canvas.width - 20, canvas.height - 20);
        ctx.lineTo(canvas.width - 80, canvas.height - 20);
        ctx.lineTo(canvas.width - 20, canvas.height - 80);
        ctx.stroke();
    };

    // 处理图片上传按钮点击
    const handleUploadClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    // 处理文件选择
    const handleFileChange = (e: any) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];

            // 检查文件类型
            if (!file.type.match('image.*')) {
                showNotification('Please Select Image', 'error');
                return;
            }

            const reader = new FileReader();
            reader.onload = (event) => {
                const img = new Image();
                img.onload = () => {
                    // 绘制到Canvas
                    const canvas = canvasRef.current;
                    if (canvas) drawImageToCanvas(img, canvas);

                    // 发送图像加载事件
                    const imageLoadedEvent = new CustomEvent('imageLoaded', {
                        detail: {image: img}
                    });
                    window.dispatchEvent(imageLoadedEvent);

                    showNotification('The image has been loaded! Now select the effect for editing.', 'success');
                };
                (img as any).src = (event as any).target.result;
            };
            reader.readAsDataURL(file);
        }
    };

    // 绘制图像到Canvas
    const drawImageToCanvas = (img: HTMLImageElement, canvas: HTMLCanvasElement) => {
        const ctx = canvas.getContext('2d');
        if (!ctx) return ;

        // 计算缩放比例以保持宽高比
        const scale = Math.min(
            canvasSize / img.width,
            canvasSize / img.height
        );

        const width = img.width * scale;
        const height = img.height * scale;

        canvas.width = width;
        canvas.height = height;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, width, height);
    };

    // 打开演示视频
    const handleDemoClick = () => {
        showNotification('The demonstration function is about to be launched, stay tuned!', 'success');
    };


    return (
        <section className="py-16 px-4 mt-[5%]">
            <div className="container mx-auto flex flex-col md:flex-row items-center">
                <div className="md:w-1/2 mb-10 md:mb-0">
                    <h1 className={`text-4xl md:text-5xl font-bold mb-4 ${isLight ? 'text-gray-800' : 'text-gray-600'}`}>
                        Unleash your
                        <span className="text-indigo-600">&nbsp;creative&nbsp;</span>potential
                    </h1>
                    <p className={`text-lg ${isLight ? 'text-gray-600' : 'text-gray-300'} mb-8`}>
                        {BrandName} provides a range of innovative image processing tools to rejuvenate your photos. From art filters to advanced editing, everything is at your fingertips.
                    </p>
                    <div className="flex space-x-4 justify-center text-[12pt]">
                        <button
                            id="uploadBtn"
                            className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition flex items-center"
                            onClick={handleUploadClick}
                        >
                            <i className="fas fa-upload mr-2"></i> Upload Photo
                        </button>
                        <button
                            className="px-6 py-3 border border-indigo-600 text-indigo-600 rounded-lg font-medium hover:bg-indigo-50 transition flex items-center"
                            onClick={handleDemoClick}
                        >
                            <i className="fas fa-upload mr-2"></i> View Demo
                        </button>
                    </div>
                    <input
                        type="file"
                        ref={fileInputRef}
                        id="fileInput"
                        accept="image/*"
                        className="hidden"
                        onChange={handleFileChange}
                    />
                </div>
                <div className="md:w-1/2 flex justify-center">
                    <div className="home-canvas-container floating">
                        <canvas
                            ref={canvasRef}
                            id="mainCanvas"
                            width={canvasSize}
                            height={canvasSize}
                            className="bg-white w-[100%] h-[100%]"
                        ></canvas>
                    </div>
                </div>
            </div>
        </section>
    );
};
